import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage, useAuth, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Building2, CreditCard, Shield, AlertTriangle, 
  CheckCircle, XCircle, Loader2, Eye, Ban, ChevronRight, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ShareAnalytics } from '@/components/ShareAnalytics';

// Admin Navigation
const AdminNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground transition">
              {t('admin.nav.dashboard')}
            </Link>
            <Link to="/admin/users" className="text-muted-foreground hover:text-foreground transition">
              {t('admin.nav.users')}
            </Link>
            <Link to="/admin/companies" className="text-muted-foreground hover:text-foreground transition">
              {t('admin.nav.companies')}
            </Link>
            <Link to="/admin/payments" className="text-muted-foreground hover:text-foreground transition">
              {t('admin.nav.payments')}
            </Link>
            <Link to="/admin/audit-logs" className="text-muted-foreground hover:text-foreground transition">
              {t('admin.nav.auditLogs')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <Badge variant="destructive">{t('admin.badge')}</Badge>
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Admin Dashboard
export const AdminDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const dashboardRes = await api.adminGetDashboardStats();
      const data = dashboardRes.data;
      setStats({
        totalUsers: data.overview.total_users,
        totalCompanies: data.overview.total_companies,
        totalJobs: data.overview.total_jobs,
        totalApplications: data.overview.total_applications,
        totalUnlocks: data.unlocks.total,
        unlocksLast30Days: data.unlocks.last_30_days,
        totalPayments: data.payments.total_transactions,
        totalRevenue: data.revenue.total_eur,
        revenueLast30Days: data.revenue.last_30_days_eur,
        emailsSent: data.emails.sent,
        emailsFailed: data.emails.failed,
        serviceRequests: data.service_requests.total,
        pendingRequests: data.service_requests.pending
      });
      setSystemStatus(data.system_status);
    } catch (error) {
      toast.error(t('admin.loadStatsFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNav />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <div className="grid md:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <h1 className="text-3xl font-bold mb-8">{t('admin.dashboard')}</h1>

        {/* System Status Alerts */}
        {systemStatus && (!systemStatus.email_configured || !systemStatus.webhook_secret_set) && (
          <div className="mb-6 space-y-2">
            {!systemStatus.email_configured && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm">Email service not configured. Job alerts will not be sent.</span>
              </div>
            )}
            {!systemStatus.webhook_secret_set && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm">Stripe webhook secret not set. Webhook verification is disabled.</span>
              </div>
            )}
          </div>
        )}

        {/* Share Analytics Section */}
        <ShareAnalytics />

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.stats.totalUsers')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.totalUsers || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.stats.companies')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.totalCompanies || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.stats.jobsPosted')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.totalJobs || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.stats.applications')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.totalApplications || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Revenue & Monetization */}
        <h2 className="text-xl font-semibold mb-4">{t('admin.revenue.title')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-green-500/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.revenue.totalRevenue')}</CardDescription>
              <CardTitle className="text-2xl text-green-600">€{stats?.totalRevenue?.toFixed(2)}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="bg-green-500/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.revenue.revenue30Days')}</CardDescription>
              <CardTitle className="text-2xl text-green-600">€{stats?.revenueLast30Days?.toFixed(2)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.revenue.totalUnlocks')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.totalUnlocks || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.revenue.unlocks30Days')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.unlocksLast30Days || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* System Health */}
        <h2 className="text-xl font-semibold mb-4">{t('admin.system.title')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.system.emailsSent')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.emailsSent || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.system.emailsFailed')}</CardDescription>
              <CardTitle className="text-2xl text-red-500">{stats?.emailsFailed || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.system.serviceRequests')}</CardDescription>
              <CardTitle className="text-2xl">{stats?.serviceRequests || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t('admin.system.pendingRequests')}</CardDescription>
              <CardTitle className="text-2xl text-amber-500">{stats?.pendingRequests || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="card-hover">
            <CardHeader>
              <Users className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('admin.actions.manageUsers')}</CardTitle>
              <CardDescription>{t('admin.actions.manageUsersDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/admin/users" className="w-full">
                <Button variant="outline" className="w-full" data-testid="admin-users-btn">
                  {t('admin.actions.viewUsers')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Building2 className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('admin.actions.manageCompanies')}</CardTitle>
              <CardDescription>{t('admin.actions.manageCompaniesDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/admin/companies" className="w-full">
                <Button variant="outline" className="w-full" data-testid="admin-companies-btn">
                  {t('admin.actions.viewCompanies')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CreditCard className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('admin.actions.payments')}</CardTitle>
              <CardDescription>{t('admin.actions.paymentsDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/admin/payments" className="w-full">
                <Button variant="outline" className="w-full" data-testid="admin-payments-btn">
                  {t('admin.actions.viewPayments')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <Shield className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('admin.actions.auditLogs')}</CardTitle>
              <CardDescription>{t('admin.actions.auditLogsDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/admin/audit-logs" className="w-full">
                <Button variant="outline" className="w-full" data-testid="admin-audit-logs-btn">
                  {t('admin.actions.viewLogs')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
          
          <Card className="card-hover">
            <CardHeader>
              <Share2 className="h-8 w-8 text-brand mb-2" />
              <CardTitle>{t('admin.actions.shareAnalytics')}</CardTitle>
              <CardDescription>{t('admin.actions.shareAnalyticsDesc')}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" className="w-full" data-testid="admin-shares-btn" onClick={() => document.getElementById('share-analytics')?.scrollIntoView({ behavior: 'smooth' })}>
                {t('admin.actions.viewAnalytics')}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Admin Users Page
export const AdminUsersPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.adminGetUsers({ page, limit: 20 });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (error) {
      toast.error(t('admin.loadUsersFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (userId) => {
    setActionLoading(userId);
    try {
      await api.adminVerifyUser(userId);
      toast.success(t('admin.userVerified'));
      fetchUsers();
    } catch (error) {
      toast.error(t('admin.verifyFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlock = async (userId) => {
    setActionLoading(userId);
    try {
      await api.adminBlockUser(userId);
      toast.success(t('admin.userBlocked'));
      fetchUsers();
    } catch (error) {
      toast.error(t('admin.blockFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-users-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">{t('admin.usersTitle', { count: total })}</h1>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((u) => (
              <Card key={u.id} data-testid={`admin-user-${u.id}`}>
                <CardContent className="flex justify-between items-center py-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{u.nickname}</p>
                      <Badge variant="outline" className="capitalize">{u.role}</Badge>
                      {u.verified && <Badge className="bg-green-500">{t('admin.verified')}</Badge>}
                      {u.blocked && <Badge variant="destructive">{t('admin.blocked')}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                    {u.company_name && (
                      <p className="text-sm text-muted-foreground">{t('admin.company')}: {u.company_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!u.verified && u.role !== 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleVerify(u.id)}
                        disabled={actionLoading === u.id}
                        data-testid={`verify-user-${u.id}`}
                      >
                        {actionLoading === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4 mr-1" />}
                        {t('admin.verify')}
                      </Button>
                    )}
                    {!u.blocked && u.role !== 'admin' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleBlock(u.id)}
                        disabled={actionLoading === u.id}
                        data-testid={`block-user-${u.id}`}
                      >
                        {actionLoading === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
                        {t('admin.block')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              {t('admin.previous')}
            </Button>
            <Button
              variant="outline"
              disabled={page * 20 >= total}
              onClick={() => setPage(p => p + 1)}
            >
              {t('admin.next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Payments Page
export const AdminPaymentsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPayments();
  }, [user]);

  const fetchPayments = async () => {
    try {
      const res = await api.adminGetPayments({ limit: 50 });
      setPayments(res.data.payments);
      setTotal(res.data.total);
    } catch (error) {
      toast.error(t('admin.loadPaymentsFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-payments-page">
        <h1 className="text-3xl font-bold mb-8">{t('admin.paymentsTitle', { count: total })}</h1>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : payments.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold">No payments yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.id} data-testid={`payment-${payment.id}`}>
                <CardContent className="flex justify-between items-center py-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold capitalize">{payment.type}</p>
                      {payment.plan_type && <Badge variant="outline">{payment.plan_type}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      User: {payment.user_id.slice(0, 8)}... • Session: {payment.session_id.slice(0, 8)}...
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">€{payment.amount}</p>
                    <Badge variant={payment.payment_status === 'paid' ? 'default' : 'secondary'}>
                      {payment.payment_status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Payment Success/Cancel Pages
export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [contact, setContact] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    
    const checkPaymentStatus = async (attempt = 0) => {
      if (attempt >= 5) {
        setStatus('timeout');
        return;
      }

      setAttempts(attempt + 1);

      try {
        // Try new Stripe checkout status endpoint first
        const res = await api.get(`/stripe/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === 'paid' && res.data.unlocked) {
          setStatus('success');
          // Contact info will be available on the job/profile page
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        // Continue polling
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } catch (error) {
        // Fallback to old payment status endpoint
        try {
          const res = await api.getPaymentStatus(sessionId);
          if (res.data.payment_status === 'paid') {
            setStatus('success');
          } else if (res.data.status === 'expired') {
            setStatus('expired');
          } else {
            setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
          }
        } catch (err) {
          setStatus('error');
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center" data-testid="payment-result">
        <CardContent className="pt-8 pb-6">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-brand mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">{t('payments.verifying')}</h1>
              <p className="text-muted-foreground">{t('payments.pleaseWait') || `Checking payment status... (${attempts}/5)`}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2 text-green-600">{t('payments.success')}</h1>
              <p className="text-muted-foreground mb-2">{t('payments.thankYou')}</p>
              <p className="text-sm text-muted-foreground mb-6">{t('payments.contactAvailable')}</p>
              <div className="flex flex-col gap-2">
                <Link to="/jobs">
                  <Button className="w-full" data-testid="back-to-jobs-btn">{t('jobs.backToJobs')}</Button>
                </Link>
                <Link to={user?.role === 'company' ? '/company/dashboard' : '/worker/dashboard'}>
                  <Button variant="outline" className="w-full" data-testid="go-dashboard-btn">{t('nav.dashboard')}</Button>
                </Link>
              </div>
            </>
          )}
          {status === 'expired' && (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t('payments.expired')}</h1>
              <p className="text-muted-foreground mb-6">{t('payments.expiredDesc')}</p>
              <Link to="/jobs">
                <Button variant="outline">{t('jobs.backToJobs')}</Button>
              </Link>
            </>
          )}
          {(status === 'error' || status === 'timeout') && (
            <>
              <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t('payments.unknown')}</h1>
              <p className="text-muted-foreground mb-6">
                {t('payments.unknownDesc')}
              </p>
              <Link to="/jobs">
                <Button variant="outline">{t('jobs.backToJobs')}</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PaymentCancelPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center" data-testid="payment-cancelled">
        <CardContent className="pt-8 pb-6">
          <XCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('payments.cancelled')}</h1>
          <p className="text-muted-foreground mb-6">{t('payments.cancelledDesc')}</p>
          <div className="flex flex-col gap-2">
            {jobId ? (
              <Link to={`/jobs/${jobId}`}>
                <Button className="w-full">{t('jobs.backToJob')}</Button>
              </Link>
            ) : (
              <Link to="/jobs">
                <Button className="w-full">{t('jobs.backToJobs')}</Button>
              </Link>
            )}
            <Button variant="outline" onClick={() => navigate(-1)}>{t('common.back')}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Admin Audit Logs Page
export const AdminAuditLogsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Dynamic import to avoid circular dependency
  const [AuditLogViewer, setAuditLogViewer] = useState(null);
  
  useEffect(() => {
    import('@/components/AuditLogViewer').then((module) => {
      setAuditLogViewer(() => module.AuditLogViewer);
    });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-audit-logs-page">
        {AuditLogViewer ? <AuditLogViewer isCompanyView={false} /> : (
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-32" />
            <Skeleton className="h-64" />
          </div>
        )}
      </div>
    </div>
  );
};
